import * as ROUTES from '../common/routes';
import * as C from '../common/constants';
import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import { COLORS, FONTS, FONT_WEIGHT } from '../common/theme';
import { getText, getPath } from '../common/utils/helpers';
import { useHistory, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import CircularProgress from '@material-ui/core/CircularProgress';
import SearchInput from './SearchInput';
import {
  StyleRules,
  Theme,
  createStyles,
  makeStyles,
} from '@material-ui/core/styles';

const urlPrefix = getPath();

const useStyles = makeStyles(
  (theme: Theme): StyleRules =>
    createStyles({
      homeContainerStyle: {
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      },
      btnContainerStyle: {
        width: '100%',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 0 24px 0',
      },
      btnStyle: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        maxWidth: '30%',
        minHeight: '68px',
        width: '100%',
        // padding: '20px 0',
        backgroundColor: COLORS.appSand,
        boxShadow: '11px 10px 26px -9px rgba(0, 0, 0, 0.64)',
        cursor: 'pointer',
        textAlign: 'center',
        fontWeight: FONT_WEIGHT.bold,
        color: COLORS.appBrown,
        fontSize: '20px',
        fontFamily: FONTS.source,
        '@media (hover: hover) and (pointer: fine)': {
          '&:hover': {
            backgroundColor: COLORS.appDarkSand,
            transition: '0.5s ease',
          },
        },
      },
      transparentStyle: {
        backgroundColor: 'rgba(0,0,0,0) !important',
        boxShadow: 'unset',
      },
      periodContainerStyle: {
        position: 'relative',
        width: '100%',
        height: 'auto',
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        boxSizing: 'border-box',
        alignItems: 'center',
        justifyContent: 'center',
      },
      periodWrapperStyle: {
        display: 'flex',
        alignItems: 'center',
        maxWidth: '306px',
        cursor: 'pointer',
        '@media (hover: hover) and (pointer: fine)': {
          '&:hover': {
            opacity: '0.7',
            transition: '0.5s ease',
          },
        },
      },
      thumbStyle: {
        width: 'auto',
        height: 'auto',
        cursor: 'pointer',
      },
      centerContainerStyle: {
        position: 'absolute',
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '30vh',
      },
      disabledStyle: {
        pointerEvents: 'none',
        opacity: 0.3,
      },
      [`@media (max-width: 800px)`]: {
        btnContainerStyle: {
          padding: '0 0 10px 0',
        },
      },
      [`@media (max-width: 600px)`]: {
        btnContainerStyle: {
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        },
        btnStyle: {
          marginBottom: '20px',
          whiteSpace: 'nowrap',
          maxWidth: '100%',
        },
      },
    })
);

const Home = (): JSX.Element => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const homeData = useSelector((state) => state.app.homeData);
  const lng = useSelector((state) => state.app.lng);
  const history = useHistory();

  const [isSearchVisible, setSearchVisible] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!homeData) {
    return (
      <div className={classes.centerContainerStyle}>
        <CircularProgress />
      </div>
    );
  }

  const onClick = (event: React.SyntheticEvent) => {
    event.preventDefault();
    event.stopPropagation();

    const itemRoute = event.currentTarget.getAttribute('data-name');
    history.push(`/${itemRoute}`);
  };

  const getPeriodBtns = ({ url, name }, index) => {
    const imgUrl: string = `${urlPrefix}${url}`;
    const correctPeriod = name.replace('btn_', '').replace('_', '-');

    return (
      <div
        className={classes.periodWrapperStyle}
        data-name={correctPeriod}
        onClick={onClick}
        key={index}
      >
        <img src={imgUrl} alt="" className={classes.thumbStyle} />
      </div>
    );
  };

  const onMapClick = (event: React.SyntheticEvent) => {
    event.preventDefault();
    event.stopPropagation();

    history.push(ROUTES.MAP);
  };

  const onAddClick = (event: React.SyntheticEvent) => {
    event.preventDefault();
    event.stopPropagation();

    history.push(ROUTES.ADD);
  };

  const onToggleHover = () => {
    setSearchVisible(!isSearchVisible);
  };

  const addLabel: string = getText(homeData?.content, 'button_add', lng);
  const searchLabel: string = getText(homeData?.content, 'button_search', lng);
  const mapLabel: string = getText(homeData?.content, 'button_map', lng);

  return (
    <div className={classes.homeContainerStyle}>
      <div className={classes.btnContainerStyle}>
        <div className={classes.btnStyle} onClick={onAddClick}>
          {addLabel}
        </div>
        <div
          className={clsx(
            classes.btnStyle,
            isSearchVisible && classes.transparentStyle
          )}
          onMouseEnter={onToggleHover}
          onMouseLeave={onToggleHover}
        >
          {isSearchVisible ? <SearchInput /> : searchLabel}
        </div>
        <div className={classes.btnStyle} onClick={onMapClick}>
          {mapLabel}
        </div>
      </div>
      <div className={classes.periodContainerStyle}>
        {homeData.images.map(getPeriodBtns)}
      </div>
    </div>
  );
};

export default Home;
